import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { lookupVat } from "../api/billing";

const titleCaseSegment = (segment) => {
  if (!segment) return segment;
  const lowered = segment.toLowerCase();
  return lowered.charAt(0).toUpperCase() + lowered.slice(1);
};

export const titleCase = (raw) => {
  if (!raw) return "";
  return String(raw)
    .split(/(\s+|[-/.,'])/)
    .map((piece) => (/^\s+$|^[-/.,']$/.test(piece) ? piece : titleCaseSegment(piece)))
    .join("")
    .trim();
};

const VatLookupDialog = ({ open, onClose, token, onAccept, onTrack }) => {
  const [vat, setVat] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    onTrack?.("vat_lookup_modal_opened", { source: "card_editor" });
    setVat("");
    setBusy(false);
    setError("");
    setPreview(null);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTimeout(() => inputRef.current?.focus(), 50);
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onTrack]);

  if (!open) return null;

  const runLookup = async () => {
    const trimmed = vat.trim();
    if (!trimmed) return;
    setBusy(true);
    setError("");
    setPreview(null);
    onTrack?.("vat_lookup_submitted", { source: "card_editor" });
    try {
      const data = await lookupVat({ token, vat: trimmed });
      const c = data.company || {};
      setPreview({
        vat: trimmed,
        ragioneSociale: c.ragioneSociale || "",
        via: titleCase(c.via),
        citta: titleCase(c.citta),
        cap: c.cap || "",
        provincia: (c.provincia || "").toUpperCase(),
        codiceUnivocoSdi: c.codiceUnivocoSdi || "",
        pec: c.pec || "",
        activityStatus: c.activityStatus || "",
        source: data.source,
      });
      onTrack?.("vat_lookup_succeeded", {
        source: "card_editor",
        resultSource: data.source || "",
      });
    } catch (err) {
      setError(
        err.payload?.message === "INVALID_VAT"
          ? "Partita IVA non valida (servono 11 cifre)."
          : err.payload?.message === "NOT_FOUND"
            ? "Nessuna azienda trovata per questa Partita IVA."
            : err.message,
      );
      onTrack?.("vat_lookup_failed", {
        source: "card_editor",
        reason: err.payload?.message || "request_failed",
        statusCode: err.statusCode,
      });
    } finally {
      setBusy(false);
    }
  };

  const acceptPreview = () => {
    if (!preview) return;
    onAccept({
      vat: preview.vat,
      ragioneSociale: preview.ragioneSociale,
      via: preview.via,
      citta: preview.citta,
      cap: preview.cap,
      provincia: preview.provincia,
      codiceUnivocoSdi: preview.codiceUnivocoSdi,
      pec: preview.pec,
    });
    onTrack?.("vat_lookup_accepted", {
      source: "card_editor",
      hasPec: Boolean(preview.pec),
      hasSdi: Boolean(preview.codiceUnivocoSdi),
    });
    onClose();
  };

  const closeDialog = () => {
    onTrack?.("vat_lookup_closed", {
      source: "card_editor",
      hadPreview: Boolean(preview),
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-3 py-6">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          <Icon icon="mdi:office-building-marker-outline" className="h-7 w-7 text-emerald-600" />
          <div>
            <h2 className="text-lg font-bold text-slate-900">Cerca azienda</h2>
            <p className="text-xs text-slate-500">
              Recupera ragione sociale, indirizzo e dati di fatturazione dalla Partita IVA.
            </p>
          </div>
          <button
            type="button"
            onClick={closeDialog}
            className="ml-auto rounded-full p-2 text-slate-400 hover:bg-slate-100"
            aria-label="Chiudi"
          >
            <Icon icon="mdi:close" className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <label className="block">
            <span className="text-xs font-bold text-slate-700">Partita IVA</span>
            <div className="mt-1 flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={vat}
                onChange={(e) => setVat(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    runLookup();
                  }
                }}
                placeholder="11 cifre, opzionale prefisso IT"
                className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={runLookup}
                disabled={busy || !vat.trim()}
                className="inline-flex items-center justify-center gap-1 rounded-md bg-slate-800 px-4 py-2 text-xs font-bold text-white hover:bg-slate-700 disabled:opacity-50"
              >
                <Icon icon="mdi:magnify" className="text-base" />
                {busy ? "Cerco..." : "Cerca"}
              </button>
            </div>
          </label>

          {error && (
            <div className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          {preview && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm">
              <p className="text-xs font-bold uppercase text-emerald-700 mb-2">
                Anteprima ({preview.source === "cache" ? "cache" : "live"})
              </p>
              <dl className="grid gap-1">
                <RowItem label="Ragione sociale" value={preview.ragioneSociale} />
                <RowItem label="Indirizzo" value={preview.via} />
                <RowItem
                  label="Citta"
                  value={[preview.cap, preview.citta, preview.provincia ? `(${preview.provincia})` : ""]
                    .filter(Boolean)
                    .join(" ")}
                />
                <RowItem label="Codice SDI" value={preview.codiceUnivocoSdi} />
                <RowItem label="PEC" value={preview.pec} />
                {preview.activityStatus && (
                  <RowItem label="Stato" value={preview.activityStatus} />
                )}
              </dl>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t px-6 py-4 bg-slate-50">
          <button
            type="button"
            onClick={closeDialog}
            className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={acceptPreview}
            disabled={!preview}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            Usa questi dati
          </button>
        </div>
      </div>
    </div>
  );
};

const RowItem = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-1 border-b border-emerald-100 last:border-0">
    <dt className="text-xs font-semibold text-slate-600">{label}</dt>
    <dd className="text-sm text-slate-900 text-right break-words">
      {value || <span className="text-slate-400">—</span>}
    </dd>
  </div>
);

export default VatLookupDialog;
